import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import API from "../../services/api.ts";
import { AIModelSelector, ExecutionTracker } from "../ai_models";
import "./ViewerNavbar.css";
import { isAllowedMessageOrigin, viewerTargetOrigin } from '../../utils/safeUrl';

const ViewerNavbar = ({
    studyId,
    studyName,
    labels = [],
    labelDisplayMode = 'dropdown',
    questions = [],
    scores = [],
    patientId,
    imageId,
    shareAnnotations = false,
    studyInstanceUID = null,
    // New props for annotation hierarchy
    currentDataType = '',
    currentSeriesId = '',
    availableDataTypes = [],
    availableSeries = [],
    // New props for multi-patient navigation
    patientIds = [],
    currentPatientIndex = 0,
    onPatientChange = null,
    isNavigating = false,
    // New props for signal tools
    showTools = false,
    signalRecords = [],
    selectedSignalIds = [],
    onLoadSignalAnnotations = null,
    // Per-study toggle map for which navbar buttons to render. Missing keys
    // default to true (full UI), so studies without a saved config behave the
    // same as before.
    viewerNavbarConfig = {},
    // Optional async callback called before navigating away (e.g. to flush
    // trame state). No-op when not in a trame session.
    onBeforeLeave = null,
    // Trame-only: toggle vertical flip for the current patient (handles
    // arthrex-style 2D stacks whose raw pixel data is anatomically inverted).
    onToggleTrameOrientation = null,
}) => {
    // Helper: a feature is shown unless the study explicitly turned it off.
    const navbarShows = (key) => viewerNavbarConfig[key] !== false;
    const navigate = useNavigate();
    const [selectedLabels, setSelectedLabels] = useState([]);
    const [questionAnswers, setQuestionAnswers] = useState({});
    const [scoreAnswers, setScoreAnswers] = useState({});
    const [expandedQuestion, setExpandedQuestion] = useState(null);
    const [expandedScore, setExpandedScore] = useState(null);
    const [showLabelsDropdown, setShowLabelsDropdown] = useState(false);
    const [labelsPanelOpen, setLabelsPanelOpen] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [initialLoaded, setInitialLoaded] = useState(false);

    // Ref for labels dropdown click-outside detection
    const labelsDropdownRef = useRef(null);

    // Annotation hierarchy state
    const [selectedAnnotationLevel, setSelectedAnnotationLevel] = useState('patient'); // 'patient', 'data_type', 'series'
    const [activeDataType, setActiveDataType] = useState(currentDataType || '');
    const [activeSeriesId, setActiveSeriesId] = useState(currentSeriesId || '');

    // Shared annotations state
    const [sharedAnnotations, setSharedAnnotations] = useState([]);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [showSharedAnnotations, setShowSharedAnnotations] = useState(false);

    // Request input state
    const [requestInput, setRequestInput] = useState("");
    const [isProcessingRequest, setIsProcessingRequest] = useState(false);
    const [requestProgress, setRequestProgress] = useState(0);
    const [requestStatus, setRequestStatus] = useState("");
    const progressIntervalRef = useRef(null);

    // AI Models execution state
    const [activeExecutions, setActiveExecutions] = useState([]);

    // Tools state
    const [showToolsDropdown, setShowToolsDropdown] = useState(false);
    const [showToolConfig, setShowToolConfig] = useState(false);
    const [selectedTool, setSelectedTool] = useState(null); // Track which tool was selected
    const [selectedSignalId, setSelectedSignalId] = useState('');
    const [selectedChannelIndex, setSelectedChannelIndex] = useState('');
    const [manualSamplingFreq, setManualSamplingFreq] = useState('');
    const [toolParams, setToolParams] = useState({}); // Dynamic tool parameters
    const [isSubmittingHRTask, setIsSubmittingHRTask] = useState(false);
    const toolsDropdownRef = useRef(null);
    const [assignedTools, setAssignedTools] = useState([]);
    const [loadingAssignedTools, setLoadingAssignedTools] = useState(false);

    // Tool execution tracking (similar to AI models)
    const [showToolExecutionsDropdown, setShowToolExecutionsDropdown] = useState(false);
    const [toolExecutions, setToolExecutions] = useState([]);
    const [elapsedTimes, setElapsedTimes] = useState({});
    const toolExecutionsDropdownRef = useRef(null);

    // Annotation loading state
    const [showAnnotationsDropdown, setShowAnnotationsDropdown] = useState(false);
    const [availableAnnotations, setAvailableAnnotations] = useState([]);
    const [selectedAnnotationIds, setSelectedAnnotationIds] = useState([]);
    const [isLoadingAnnotations, setIsLoadingAnnotations] = useState(false);
    const [isFetchingAnnotations, setIsFetchingAnnotations] = useState(false);
    const annotationsDropdownRef = useRef(null);

    // Maps loading state
    const [showMapsDropdown, setShowMapsDropdown] = useState(false);
    const [availableMaps, setAvailableMaps] = useState([]);
    const [selectedMapIds, setSelectedMapIds] = useState([]);
    const [isLoadingMaps, setIsLoadingMaps] = useState(false);
    const [isFetchingMaps, setIsFetchingMaps] = useState(false);
    const mapsDropdownRef = useRef(null);

    // Mark-as-annotated checkbox state
    const [markedAsAnnotated, setMarkedAsAnnotated] = useState(false);
    const [isTogglingAnnotated, setIsTogglingAnnotated] = useState(false);

    // OHIF "Save Annotations" button state — captures measurements + segmentations
    const [isSavingOhif, setIsSavingOhif] = useState(false);

    const logoSrc = "/assets/logo_no_background.png";

    // Reader-queue navigation (Previous / Next case). Only fetched when the
    // study's navbar config enables it. The queue endpoint is randomized-only
    // server-side, so a 400 just leaves us with no buttons rendered.
    const queueNavEnabled = navbarShows('queue_nav');
    const [queueRows, setQueueRows] = useState([]);
    const [queueLoading, setQueueLoading] = useState(false);
    const [queueNavigating, setQueueNavigating] = useState(false);
    useEffect(() => {
        if (!queueNavEnabled || !studyId) {
            setQueueRows([]);
            return;
        }
        let cancelled = false;
        setQueueLoading(true);
        API.get(`/studies/${studyId}/my-queue/`)
            .then((res) => {
                if (cancelled) return;
                setQueueRows(Array.isArray(res.data?.queue) ? res.data.queue : []);
            })
            .catch(() => {
                if (!cancelled) setQueueRows([]);
            })
            .finally(() => {
                if (!cancelled) setQueueLoading(false);
            });
        return () => { cancelled = true; };
    }, [queueNavEnabled, studyId, patientId]);

    const queueIndex = queueRows.findIndex((q) => q.patient_id === patientId);
    const queueLength = queueRows.length;
    const queueAtStart = queueIndex <= 0;
    const queueAtEnd = queueLength > 0 && queueIndex >= queueLength - 1;

    const navigateToQueuePatient = (newPatientId) => {
        if (!newPatientId) return;
        const url = new URL(window.location.href);
        url.searchParams.set('patient_id', newPatientId);
        url.searchParams.delete('patient_ids');
        url.searchParams.delete('current_index');
        window.location.href = url.toString();
    };

    const handleQueuePrev = () => {
        if (queueAtStart) return;
        const prev = queueRows[queueIndex - 1];
        if (prev?.patient_id) navigateToQueuePatient(prev.patient_id);
    };

    const handleQueueNext = async () => {
        if (queueNavigating) return;
        setQueueNavigating(true);
        try {
            try {
                await API.post(`/studies/${studyId}/case-completed/${encodeURIComponent(patientId)}/`);
            } catch (err) {
                // Marking complete is best-effort — keep moving so the
                // reviewer isn't blocked by a transient backend hiccup.
                console.warn('[ViewerNavbar] case-completed failed:', err);
            }
            // Re-fetch queue so the next_patient_id reflects the just-completed
            // state (and any admin-driven queue changes since the page loaded).
            try {
                const res = await API.get(`/studies/${studyId}/my-queue/`);
                const nextId = res.data?.next_patient_id;
                if (nextId) {
                    navigateToQueuePatient(nextId);
                    return;
                }
                toast.success('All cases completed for this study.');
            } catch (err) {
                console.warn('[ViewerNavbar] queue refresh failed:', err);
                toast.error('Could not advance to the next case.');
            }
        } finally {
            setQueueNavigating(false);
        }
    };

    // Click-outside handler for labels dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (labelsDropdownRef.current && !labelsDropdownRef.current.contains(event.target)) {
                setShowLabelsDropdown(false);
            }
        };

        if (showLabelsDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showLabelsDropdown]);

    // Click-outside handler for annotations dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (annotationsDropdownRef.current && !annotationsDropdownRef.current.contains(event.target)) {
                setShowAnnotationsDropdown(false);
            }
        };

        if (showAnnotationsDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showAnnotationsDropdown]);

    // Click-outside handler for maps dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (mapsDropdownRef.current && !mapsDropdownRef.current.contains(event.target)) {
                setShowMapsDropdown(false);
            }
        };

        if (showMapsDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showMapsDropdown]);

    // Click-outside handler for tools dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (toolsDropdownRef.current && !toolsDropdownRef.current.contains(event.target)) {
                setShowToolsDropdown(false);
            }
        };

        if (showToolsDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showToolsDropdown]);

    // Click-outside handler for tool executions dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (toolExecutionsDropdownRef.current && !toolExecutionsDropdownRef.current.contains(event.target)) {
                setShowToolExecutionsDropdown(false);
            }
        };

        if (showToolExecutionsDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showToolExecutionsDropdown]);

    // Update elapsed time for running tasks every second
    useEffect(() => {
        const runningTasks = toolExecutions.filter(exec => exec.status === 'running');

        if (runningTasks.length === 0) {
            return; // No running tasks, no need for interval
        }

        const interval = setInterval(() => {
            const newElapsedTimes = {};
            runningTasks.forEach(exec => {
                const elapsed = Math.floor((Date.now() - exec.startTime.getTime()) / 1000); // seconds
                newElapsedTimes[exec.id] = elapsed;
            });
            setElapsedTimes(newElapsedTimes);
        }, 1000); // Update every second

        return () => clearInterval(interval);
    }, [toolExecutions]);

    // Fetch assigned tools for this study
    useEffect(() => {
        if (!studyId) return;
        const fetchAssignedTools = async () => {
            try {
                setLoadingAssignedTools(true);
                const response = await API.get('/ai-models/models/', {
                    params: { study: studyId, entity_type: 'tool' }
                });
                setAssignedTools(response.data);
            } catch (err) {
                console.error('Error fetching assigned tools:', err);
            } finally {
                setLoadingAssignedTools(false);
            }
        };
        fetchAssignedTools();
    }, [studyId]);

    // Fetch available annotations when dropdown opens or patient changes
    // Stable reference to signalRecords to avoid infinite re-render loop
    // (default [] creates a new reference each render, causing useCallback to recreate)
    const signalRecordsRef = useRef(signalRecords);
    signalRecordsRef.current = signalRecords;

    const fetchAvailableAnnotations = useCallback(async () => {
        if (!patientId) return;

        setIsFetchingAnnotations(true);
        try {
            // Build query params - include study_id to filter annotations by study and user
            let url = `/viewers/get_patient_annotations/?patient_id=${encodeURIComponent(patientId)}`;
            if (studyId) {
                url += `&study_id=${encodeURIComponent(studyId)}`;
            }
            const currentSignalRecords = signalRecordsRef.current;
            if (currentSignalRecords && currentSignalRecords.length > 0) {
                url += `&signal_data_id=${encodeURIComponent(currentSignalRecords[0].id)}`;
            }
            const response = await API.get(url);
            setAvailableAnnotations(response.data.annotations || []);
        } catch (err) {
            console.error("Error fetching annotations:", err);
            toast.error("Failed to fetch annotations");
        } finally {
            setIsFetchingAnnotations(false);
        }
    }, [patientId, studyId]);

    // Fetch annotations when dropdown opens
    useEffect(() => {
        if (showAnnotationsDropdown) {
            fetchAvailableAnnotations();
        }
    }, [showAnnotationsDropdown, fetchAvailableAnnotations]);

    // Fetch available maps when dropdown opens or patient changes
    const fetchAvailableMaps = useCallback(async () => {
        if (!patientId) return;

        setIsFetchingMaps(true);
        try {
            let url = `/viewers/get_patient_maps/?patient_id=${encodeURIComponent(patientId)}`;
            if (studyId) {
                url += `&study_id=${encodeURIComponent(studyId)}`;
            }
            const response = await API.get(url);
            setAvailableMaps(response.data.maps || []);
        } catch (err) {
            console.error("Error fetching maps:", err);
            toast.error("Failed to fetch maps");
        } finally {
            setIsFetchingMaps(false);
        }
    }, [patientId, studyId]);

    // Fetch maps when dropdown opens
    useEffect(() => {
        if (showMapsDropdown) {
            fetchAvailableMaps();
        }
    }, [showMapsDropdown, fetchAvailableMaps]);

    // Reset selected annotations when patient changes
    useEffect(() => {
        setSelectedAnnotationIds([]);
        setAvailableAnnotations([]);
        setSelectedMapIds([]);
        setAvailableMaps([]);
    }, [patientId]);

    // Toggle annotation selection
    const toggleAnnotationSelection = (annId) => {
        setSelectedAnnotationIds(prev => {
            if (prev.includes(annId)) {
                return prev.filter(id => id !== annId);
            } else {
                return [...prev, annId];
            }
        });
    };

    // Load selected annotations to Orthanc (or to signal timeline for signal viewer)
    const handleLoadAnnotations = async () => {
        if (selectedAnnotationIds.length === 0) {
            toast.warning("Please select at least one annotation to load");
            return;
        }

        // Signal viewer mode: load JSON event annotations onto the timeline
        if (onLoadSignalAnnotations) {
            setIsLoadingAnnotations(true);
            try {
                await onLoadSignalAnnotations(selectedAnnotationIds);
                setShowAnnotationsDropdown(false);
                setSelectedAnnotationIds([]);
            } catch (err) {
                console.error("Error loading signal annotations:", err);
                toast.error("Failed to load signal annotations");
            } finally {
                setIsLoadingAnnotations(false);
            }
            return;
        }

        setIsLoadingAnnotations(true);
        const loadingToast = toast.loading("Converting and loading annotations...");

        try {
            const response = await API.post('/viewers/load_annotations_to_orthanc/', {
                annotation_ids: selectedAnnotationIds,
                patient_id: patientId,
                study_instance_uid: studyInstanceUID,
            });

            toast.dismiss(loadingToast);

            if (response.data.loaded && response.data.loaded.length > 0) {
                toast.success(
                    <div>
                        <strong>Annotations Loaded!</strong>
                        <p style={{ margin: '5px 0 0 0', fontSize: '0.9em' }}>
                            {response.data.message}. Refreshing viewer...
                        </p>
                    </div>,
                    { autoClose: 3000 }
                );

                // Close dropdown and refresh page after a short delay
                setShowAnnotationsDropdown(false);
                setSelectedAnnotationIds([]);

                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            } else if (response.data.errors && response.data.errors.length > 0) {
                toast.error(`Failed to load some annotations: ${response.data.errors[0].error}`);
            }
        } catch (err) {
            toast.dismiss(loadingToast);
            console.error("Error loading annotations:", err);
            toast.error(err.response?.data?.error || "Failed to load annotations");
        } finally {
            setIsLoadingAnnotations(false);
        }
    };

    // Toggle map selection
    const toggleMapSelection = (mapId) => {
        setSelectedMapIds(prev => {
            if (prev.includes(mapId)) {
                return prev.filter(id => id !== mapId);
            } else {
                return [...prev, mapId];
            }
        });
    };

    // Check if the current viewer is PME (pathology)
    const isPmeViewer = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const viewerUrl = urlParams.get('viewer_url') || '';
        return decodeURIComponent(viewerUrl).includes('/pme/');
    };

    // Load selected maps — PME uses postMessage, others use Orthanc conversion
    const handleLoadMaps = async () => {
        if (selectedMapIds.length === 0) {
            toast.warning("Please select at least one map to load");
            return;
        }

        // For PME viewer: send map file paths directly to the iframe via postMessage
        if (isPmeViewer()) {
            const selectedMaps = availableMaps.filter(m => selectedMapIds.includes(m.id));
            const iframe = document.querySelector('.dv-viewer-iframe');
            if (iframe && iframe.contentWindow) {
                selectedMaps.forEach(m => {
                    const filePath = m.container_path || '';
                    if (filePath) {
                        const targetOrigin = viewerTargetOrigin(iframe);
                        if (targetOrigin) {
                            iframe.contentWindow.postMessage({
                                type: 'load_map',
                                mapId: m.id,
                                filePath: filePath,
                                name: m.filename,
                                parentSlide: m.parent_filename || '',
                            }, targetOrigin);
                        }
                    }
                });
                toast.success(`Loaded ${selectedMaps.length} map(s) as overlay`);
                setShowMapsDropdown(false);
                setSelectedMapIds([]);
            } else {
                toast.error("Could not communicate with the viewer iframe");
            }
            return;
        }

        setIsLoadingMaps(true);
        const loadingToast = toast.loading("Converting and loading maps...");

        try {
            const response = await API.post('/viewers/load_maps_to_orthanc/', {
                map_ids: selectedMapIds,
                patient_id: patientId,
                study_instance_uid: studyInstanceUID,
            });

            toast.dismiss(loadingToast);

            if (response.data.loaded && response.data.loaded.length > 0) {
                toast.success(
                    <div>
                        <strong>Maps Loaded!</strong>
                        <p style={{ margin: '5px 0 0 0', fontSize: '0.9em' }}>
                            {response.data.message}. Refreshing viewer...
                        </p>
                    </div>,
                    { autoClose: 3000 }
                );

                setShowMapsDropdown(false);
                setSelectedMapIds([]);

                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            } else if (response.data.errors && response.data.errors.length > 0) {
                toast.error(`Failed to load some maps: ${response.data.errors[0].error}`);
            }
        } catch (err) {
            toast.dismiss(loadingToast);
            console.error("Error loading maps:", err);
            toast.error(err.response?.data?.error || "Failed to load maps");
        } finally {
            setIsLoadingMaps(false);
        }
    };

    // Debug logging
    console.log('ViewerNavbar props:', { studyId, studyName, labelsCount: labels.length, questionsCount: questions.length, scoresCount: scores.length, patientId, initialLoaded, hasChanges });

    // Start annotation timer when patient is opened
    const startAnnotationTimer = useCallback(async () => {
        if (!studyId || !patientId) return;

        try {
            await API.post(`/studies/${studyId}/start-timer/${encodeURIComponent(patientId)}/`, {
                data_type: activeDataType,
                series_id: activeSeriesId
            });
            console.log("Annotation timer started for patient:", patientId, "dataType:", activeDataType, "seriesId:", activeSeriesId);
        } catch (err) {
            console.error("Error starting annotation timer:", err);
        }
    }, [studyId, patientId, activeDataType, activeSeriesId]);

    // Load existing annotation for this patient
    const loadExistingAnnotation = useCallback(async () => {
        if (!studyId || !patientId) return;

        try {
            const params = new URLSearchParams();
            if (activeDataType) params.append('data_type', activeDataType);
            if (activeSeriesId) params.append('series_id', activeSeriesId);
            const queryString = params.toString();
            const url = `/studies/${studyId}/annotation/${encodeURIComponent(patientId)}/${queryString ? '?' + queryString : ''}`;

            const response = await API.get(url);
            if (response.data) {
                setSelectedLabels(response.data.selected_labels || []);
                setQuestionAnswers(response.data.question_answers || {});
                setScoreAnswers(response.data.score_answers || {});
                setMarkedAsAnnotated(!!response.data.annotation_completed_at);
            }
        } catch (err) {
            // 404 is expected if no annotation exists yet
            if (err.response?.status !== 404) {
                console.error("Error loading annotation:", err);
            }
            // Reset state for new annotations
            setSelectedLabels([]);
            setQuestionAnswers({});
            setScoreAnswers({});
            setMarkedAsAnnotated(false);
        } finally {
            setInitialLoaded(true);
        }
    }, [studyId, patientId, activeDataType, activeSeriesId]);

    useEffect(() => {
        // Start the timer first, then load existing annotation
        startAnnotationTimer();
        loadExistingAnnotation();
    }, [startAnnotationTimer, loadExistingAnnotation]);

    // Toggle the manual "Mark as Annotated" flag for the current patient
    const toggleMarkedAsAnnotated = useCallback(async () => {
        if (!studyId || !patientId || isTogglingAnnotated) return;
        const next = !markedAsAnnotated;
        setIsTogglingAnnotated(true);
        // Optimistic update
        setMarkedAsAnnotated(next);
        try {
            await API.post(
                `/studies/${studyId}/mark-annotated/${encodeURIComponent(patientId)}/`,
                {
                    mark_complete: next,
                    data_type: activeDataType,
                    series_id: activeSeriesId,
                }
            );
            toast.success(next ? "Marked as annotated" : "Unmarked as annotated");
        } catch (err) {
            console.error("Error toggling mark-as-annotated:", err);
            toast.error(err.response?.data?.error || "Failed to update annotation status");
            setMarkedAsAnnotated(!next);
        } finally {
            setIsTogglingAnnotated(false);
        }
    }, [studyId, patientId, markedAsAnnotated, isTogglingAnnotated, activeDataType, activeSeriesId]);

    // Save OHIF measurements + segmentations.
    //
    // Cross-document call to the OHIF iframe via postMessage; the iframe's
    // patched IIFE responds with the measurements snapshot and STOWs each
    // segmentation to Orthanc (orthanc_seg_callback then materializes the
    // MedicalImage row in Discovery).
    //
    // After the response arrives, we POST the measurements to
    // /studies/<id>/save-ohif-measurements/<patient_id>/ and ping
    // /viewers/sync_segmentations_from_orthanc/ as a safety net in case the
    // Orthanc Lua callback hasn't fired yet on this VM.
    const handleSaveOhifAnnotations = useCallback(async () => {
        if (!studyId || !patientId || isSavingOhif) return;

        const iframe = document.querySelector('.dv-viewer-iframe');
        if (!iframe || !iframe.contentWindow) {
            toast.error("OHIF viewer not ready");
            return;
        }

        setIsSavingOhif(true);
        const savingToast = toast.loading("Saving annotations...");
        const requestId = `dv-save-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const TIMEOUT_MS = 60000;

        const result = await new Promise((resolve) => {
            let timer = null;
            const onMsg = (ev) => {
            // Reject cross-origin messages. Without this, any page loaded in the viewer
            // iframe -- or any other frame -- can drive this handler.
            if (!isAllowedMessageOrigin(ev.origin)) return;
                const d = ev.data;
                if (!d || d.type !== 'dv_save_annotations_response' || d.requestId !== requestId) return;
                window.removeEventListener('message', onMsg);
                if (timer) clearTimeout(timer);
                resolve(d);
            };
            window.addEventListener('message', onMsg);
            timer = setTimeout(() => {
                window.removeEventListener('message', onMsg);
                resolve({ ok: false, error: 'Timed out waiting for OHIF response' });
            }, TIMEOUT_MS);
            try {
                const targetOrigin = viewerTargetOrigin(iframe);
                if (!targetOrigin) throw new Error('viewer iframe origin not allowed');
                iframe.contentWindow.postMessage({ type: 'dv_save_annotations_request', requestId }, targetOrigin);
            } catch (e) {
                window.removeEventListener('message', onMsg);
                if (timer) clearTimeout(timer);
                resolve({ ok: false, error: String(e?.message || e) });
            }
        });

        toast.dismiss(savingToast);

        if (!result.ok) {
            toast.error(result.error || "Failed to save annotations");
            setIsSavingOhif(false);
            return;
        }

        const measurements = Array.isArray(result.measurements) ? result.measurements : [];
        const segInfo = result.segmentations || { count: 0, stored: 0, errors: [] };

        try {
            await API.post(
                `/studies/${studyId}/save-ohif-measurements/${encodeURIComponent(patientId)}/`,
                {
                    measurements,
                    data_type: activeDataType,
                    series_id: activeSeriesId,
                }
            );
        } catch (err) {
            console.error("save-ohif-measurements failed:", err);
            toast.error(err.response?.data?.error || "Failed to save measurements");
            setIsSavingOhif(false);
            return;
        }

        // Make sure the SEG rows make it into Discovery even if the Orthanc Lua
        // callback didn't fire. Backed off best-effort; surfaced errors don't
        // block the success toast since the measurements already saved.
        if (segInfo.stored > 0 && studyInstanceUID) {
            try {
                await API.post(`/viewers/sync_segmentations_from_orthanc/`, {
                    study_instance_uid: studyInstanceUID,
                    study_id: studyId,
                });
            } catch (err) {
                console.warn("sync_segmentations_from_orthanc failed:", err);
            }
        }

        const segMsg = segInfo.count > 0
            ? `, ${segInfo.stored}/${segInfo.count} segmentation(s)`
            : '';
        const errMsg = (segInfo.errors && segInfo.errors.length)
            ? ` (${segInfo.errors.length} seg error${segInfo.errors.length > 1 ? 's' : ''})`
            : '';
        toast.success(`Saved ${measurements.length} measurement(s)${segMsg}${errMsg}`);
        setIsSavingOhif(false);
    }, [studyId, patientId, isSavingOhif, activeDataType, activeSeriesId, studyInstanceUID]);

    // Rehydrate persisted OHIF measurements when the iframe IIFE signals ready.
    // Mirror of handleSaveOhifAnnotations: GET /studies/<id>/ohif-measurements/<patient_id>/
    // and post `dv_load_annotations_request` to the iframe so the dvLoadAnn IIFE
    // can call measurementService.addRawMeasurement for each entry.
    const loadedKeysRef = useRef(new Set());
    useEffect(() => {
        loadedKeysRef.current = new Set();
    }, [studyId, patientId]);
    useEffect(() => {
        if (!studyId || !patientId) return undefined;
        const cacheKey = `${studyId}::${patientId}::${activeDataType || ''}::${activeSeriesId || ''}`;

        const onReady = async (ev) => {
            // Reject cross-origin messages. Without this, any page loaded in the
            // viewer iframe -- or any other frame -- can drive this handler.
            if (!isAllowedMessageOrigin(ev.origin)) return;
            const d = ev.data;
            if (!d || d.type !== 'dv_load_annotations_ready') return;
            if (loadedKeysRef.current.has(cacheKey)) return;
            loadedKeysRef.current.add(cacheKey);

            let measurements = [];
            try {
                const params = new URLSearchParams();
                if (activeDataType) params.set('data_type', activeDataType);
                if (activeSeriesId) params.set('series_id', activeSeriesId);
                const qs = params.toString();
                const url = `/studies/${studyId}/ohif-measurements/${encodeURIComponent(patientId)}/${qs ? '?' + qs : ''}`;
                const res = await API.get(url);
                measurements = Array.isArray(res.data?.measurements) ? res.data.measurements : [];
            } catch (err) {
                console.warn("[dvLoadAnn] fetch failed:", err.response?.status, err.message);
                return;
            }
            if (!measurements.length) {
                console.log("[dvLoadAnn] no saved measurements for", studyId, patientId);
                return;
            }
            console.log("[dvLoadAnn] fetched", measurements.length, "measurements, posting to iframe");

            const iframe = document.querySelector('.dv-viewer-iframe');
            if (!iframe || !iframe.contentWindow) return;

            const requestId = `dv-load-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            const onResponse = (ev2) => {
                // Reject cross-origin messages -- see the other listeners.
                if (!isAllowedMessageOrigin(ev2.origin)) return;
                const r = ev2.data;
                if (!r || r.type !== 'dv_load_annotations_response' || r.requestId !== requestId) return;
                window.removeEventListener('message', onResponse);
                if (r.ok && r.loaded > 0) {
                    toast.success(`Loaded ${r.loaded} saved measurement${r.loaded === 1 ? '' : 's'}`);
                } else if (r.errors && r.errors.length) {
                    console.warn("[dvLoadAnn] hydration errors:", r.errors);
                }
            };
            window.addEventListener('message', onResponse);
            setTimeout(() => window.removeEventListener('message', onResponse), 30000);
            try {
                const targetOrigin = viewerTargetOrigin(iframe);
                if (!targetOrigin) throw new Error('viewer iframe origin not allowed');
                iframe.contentWindow.postMessage({ type: 'dv_load_annotations_request', requestId, measurements }, targetOrigin);
            } catch (e) {
                window.removeEventListener('message', onResponse);
            }
        };

        window.addEventListener('message', onReady);
        return () => window.removeEventListener('message', onReady);
    }, [studyId, patientId, activeDataType, activeSeriesId]);

    // Auto-save measurements on every add/remove/update inside OHIF.
    //
    // The iframe's `dvAutoSave` IIFE subscribes to measurement-service events
    // (MEASUREMENT_ADDED/REMOVED/UPDATED/CLEARED), debounces ~500 ms, and
    // posts the full current measurement set as `dv_autosave_measurements`.
    // We POST it silently to the same endpoint the manual Save button uses —
    // that endpoint replaces the stored set, so deletions propagate without
    // any explicit delete call. The Project page Annotations list re-fetches
    // on navigation and will reflect the updated set.
    useEffect(() => {
        if (!studyId || !patientId) return undefined;

        const onAutoSave = async (ev) => {
            // Reject cross-origin messages. Without this, any page loaded in the viewer
            // iframe -- or any other frame -- can drive this handler.
            if (!isAllowedMessageOrigin(ev.origin)) return;
            const d = ev.data;
            if (!d || d.type !== 'dv_autosave_measurements') return;
            const measurements = Array.isArray(d.measurements) ? d.measurements : [];
            try {
                await API.post(
                    `/studies/${studyId}/save-ohif-measurements/${encodeURIComponent(patientId)}/`,
                    {
                        measurements,
                        data_type: activeDataType,
                        series_id: activeSeriesId,
                    }
                );
                console.log('[dvAutoSave] persisted', measurements.length, 'measurement(s)');
            } catch (err) {
                console.warn('[dvAutoSave] persist failed:', err.response?.status, err.message);
            }
        };

        window.addEventListener('message', onAutoSave);
        return () => window.removeEventListener('message', onAutoSave);
    }, [studyId, patientId, activeDataType, activeSeriesId]);

    // Delete a saved SEG series from the OHIF Study Browser 3-dot menu.
    //
    // The iframe's `dvDelSeg` IIFE registers a "Delete" item in
    // `studyBrowser.thumbnailMenuItems`. When clicked on a SEG it confirm()s,
    // then posts `dv_delete_segmentation_request` with StudyInstanceUID +
    // SeriesInstanceUID. We POST to the existing endpoint
    // `/api/viewers/delete_segmentation_series/` which removes the series
    // from Orthanc, MedicalImage rows, and cascade-cleans PatientData.
    // On success we post back `dv_delete_segmentation_complete` so the IIFE
    // can scrub the matching in-memory segmentation + displaySet without a
    // full viewer reload.
    useEffect(() => {
        if (!studyId || !patientId) return undefined;

        const onDeleteRequest = async (ev) => {
            // Reject cross-origin messages. Without this, any page loaded in the viewer
            // iframe -- or any other frame -- can drive this handler.
            if (!isAllowedMessageOrigin(ev.origin)) return;
            const d = ev.data;
            if (!d || d.type !== 'dv_delete_segmentation_request') return;
            const { requestId, StudyInstanceUID, SeriesInstanceUID, label } = d;
            if (!StudyInstanceUID || !SeriesInstanceUID) {
                console.warn('[dvDelSeg] missing UIDs in request', d);
                return;
            }
            const deletingToast = toast.loading(`Deleting "${label || 'segmentation'}"...`);
            let ok = false;
            let err = null;
            try {
                const res = await API.post('/viewers/delete_segmentation_series/', {
                    study_instance_uid: StudyInstanceUID,
                    series_instance_uid: SeriesInstanceUID,
                });
                ok = !!res?.data?.status && res.data.status === 'ok';
                if (!ok) err = res?.data?.error || 'Unknown error';
            } catch (e) {
                err = e.response?.data?.error || e.message || String(e);
                console.error('[dvDelSeg] delete failed:', e);
            }
            toast.dismiss(deletingToast);
            if (ok) {
                toast.success(`Deleted ${label || 'segmentation'}`);
            } else {
                toast.error(`Failed to delete: ${err}`);
            }
            // Tell the iframe to clean up the in-memory state regardless of error path
            try {
                const iframe = document.querySelector('.dv-viewer-iframe');
                const targetOrigin = viewerTargetOrigin(iframe);
                if (targetOrigin) {
                    iframe.contentWindow.postMessage(
                        { type: 'dv_delete_segmentation_complete', requestId, ok },
                        targetOrigin,
                    );
                }
            } catch (postErr) {
                console.warn('[dvDelSeg] postMessage back failed:', postErr);
            }
        };

        window.addEventListener('message', onDeleteRequest);
        return () => window.removeEventListener('message', onDeleteRequest);
    }, [studyId, patientId]);

    // Load shared annotations when sharing is enabled
    const loadSharedAnnotations = useCallback(async () => {
        if (!studyId || !patientId || !shareAnnotations) return;

        try {
            const response = await API.get(`/studies/${studyId}/shared-annotations/${encodeURIComponent(patientId)}/`);
            if (response.data) {
                setSharedAnnotations(response.data.annotations || []);
                setCurrentUserId(response.data.current_user_id);
            }
        } catch (err) {
            // 403 means sharing not enabled, which is fine
            if (err.response?.status !== 403) {
                console.error("Error loading shared annotations:", err);
            }
        }
    }, [studyId, patientId, shareAnnotations]);

    // Handle annotation level change
    const handleAnnotationLevelChange = (level) => {
        // Prompt to save if there are unsaved changes
        if (hasChanges) {
            const shouldProceed = window.confirm("You have unsaved changes. Do you want to discard them?");
            if (!shouldProceed) return;
        }

        setSelectedAnnotationLevel(level);
        // Reset data type and series when changing level
        if (level === 'patient') {
            setActiveDataType('');
            setActiveSeriesId('');
        } else if (level === 'data_type') {
            setActiveSeriesId('');
            // Auto-select first data type if available
            if (effectiveAvailableDataTypes.length > 0 && !activeDataType) {
                setActiveDataType(effectiveAvailableDataTypes[0]);
            }
        } else if (level === 'series') {
            // Auto-select first data type if available
            if (effectiveAvailableDataTypes.length > 0 && !activeDataType) {
                setActiveDataType(effectiveAvailableDataTypes[0]);
            }
        }
        setInitialLoaded(false);
        setHasChanges(false);
    };

    // Handle data type change
    const handleDataTypeChange = (dataType) => {
        if (hasChanges) {
            const shouldProceed = window.confirm("You have unsaved changes. Do you want to discard them?");
            if (!shouldProceed) return;
        }

        setActiveDataType(dataType);
        setActiveSeriesId(''); // Reset series when data type changes
        setInitialLoaded(false);
        setHasChanges(false);
    };

    // Handle series change
    const handleSeriesChange = (seriesId) => {
        if (hasChanges) {
            const shouldProceed = window.confirm("You have unsaved changes. Do you want to discard them?");
            if (!shouldProceed) return;
        }

        setActiveSeriesId(seriesId);
        setInitialLoaded(false);
        setHasChanges(false);
    };

    // Get unique annotation levels from all items
    const getAvailableAnnotationLevels = () => {
        const allItems = [...labels, ...questions, ...scores];
        const levels = new Set(allItems.map(item => item.annotation_level || 'patient'));
        return Array.from(levels);
    };

    const availableAnnotationLevels = getAvailableAnnotationLevels();

    // Get effective available data types - either from props or default list
    const effectiveAvailableDataTypes = availableDataTypes.length > 0
        ? availableDataTypes
        : ['imaging', 'document']; // Default data types

    // Filter labels/questions/scores based on selected annotation level
    const getFilteredAnnotationItems = (items) => {
        return items.filter(item => {
            const level = item.annotation_level || 'patient';
            return level === selectedAnnotationLevel;
        });
    };

    const filteredLabels = getFilteredAnnotationItems(labels);
    const filteredQuestions = getFilteredAnnotationItems(questions);
    const filteredScores = getFilteredAnnotationItems(scores);

    // Check if the current level selection is valid for saving
    const isLevelSelectionComplete = () => {
        if (selectedAnnotationLevel === 'patient') return true;
        if (selectedAnnotationLevel === 'data_type') return !!activeDataType;
        if (selectedAnnotationLevel === 'series') {
            // If no series are available, allow saving at data-type level
            if (availableSeries.length === 0 && activeDataType) return true;
            return !!activeDataType && !!activeSeriesId;
        }
        return false;
    };

    useEffect(() => {
        if (shareAnnotations) {
            loadSharedAnnotations();
        }
    }, [shareAnnotations, loadSharedAnnotations]);

    // Get other users' annotations (excluding current user)
    const otherUsersAnnotations = sharedAnnotations.filter(
        ann => ann.annotated_by !== currentUserId
    );

    // Check if label is selected
    const isLabelSelected = (label) => {
        return selectedLabels.some(l => l.id === label.id);
    };

    // Multi-select if ANY label is configured with allow_multiple=true
    // Single-select only when ALL labels are single-select (or none have allow_multiple set)
    const isSingleSelectionMode = filteredLabels.length > 0 && filteredLabels.every(l => !l.allow_multiple);

    // Toggle label selection
    // If any label has allow_multiple=false, only one label can be selected at a time (single selection mode)
    // Auto-saves after label selection
    const handleToggleLabel = async (label) => {
        // Compute new labels synchronously from current state
        const isSelected = selectedLabels.some(l => l.id === label.id);
        let newLabels;
        if (isSelected) {
            // Deselecting - always allowed
            newLabels = selectedLabels.filter(l => l.id !== label.id);
        } else {
            // Selecting - check if we're in single-selection mode
            if (isSingleSelectionMode) {
                // Single selection mode: replace all labels with this one
                newLabels = [{ id: label.id, name: label.name, color: label.color }];
            } else {
                // Multi selection mode: add to existing
                newLabels = [...selectedLabels, { id: label.id, name: label.name, color: label.color }];
            }
        }

        // Update state
        setSelectedLabels(newLabels);

        // Auto-save after label change
        if (!studyId || !patientId) return;

        setIsSaving(true);
        try {
            await API.post(`/studies/${studyId}/annotate/`, {
                patient_id: patientId,
                data_type: activeDataType,
                series_id: activeSeriesId,
                selected_labels: newLabels,
                question_answers: questionAnswers,
                score_answers: scoreAnswers
            });
            // Close dropdown after successful save
            setShowLabelsDropdown(false);
        } catch (err) {
            console.error("Error auto-saving annotation:", err);
            toast.error("Failed to save label selection");
        } finally {
            setIsSaving(false);
        }
    };

    // Auto-save plumbing for question and score answers.
    //
    // `handleToggleLabel` already auto-saves labels immediately. We mirror
    // that here for answers, but with one twist: text input fires per
    // keystroke, so we debounce ~600 ms. Radio/checkbox/score-slider fire
    // once per click — those save immediately.
    //
    // Patient navigation hazard: a pending debounced save could fire AFTER
    // patientId changes and accidentally write the previous patient's answers
    // to a new patient. We close over the studyId/patientId/labels at
    // schedule time so the save always targets the right record, and the
    // cleanup useEffect below flushes any pending save when the active
    // patient changes.
    const answerSaveTimerRef = useRef(null);
    const pendingAnswerSaveRef = useRef(null);

    const scheduleAnswerSave = (nextQuestionAnswers, nextScoreAnswers, immediate = false) => {
        if (!studyId || !patientId) return;
        // Capture context at schedule time so a pending save doesn't write to
        // a different patient after navigation.
        const sid = studyId;
        const pid = patientId;
        const dt = activeDataType;
        const srid = activeSeriesId;
        const labels = selectedLabels;

        const doSave = async () => {
            pendingAnswerSaveRef.current = null;
            try {
                await API.post(`/studies/${sid}/annotate/`, {
                    patient_id: pid,
                    data_type: dt,
                    series_id: srid,
                    selected_labels: labels,
                    question_answers: nextQuestionAnswers,
                    score_answers: nextScoreAnswers,
                });
            } catch (err) {
                console.warn(
                    "[autoSave answers] failed:",
                    err.response?.status, err.message,
                );
            }
        };

        if (answerSaveTimerRef.current) {
            clearTimeout(answerSaveTimerRef.current);
            answerSaveTimerRef.current = null;
        }
        pendingAnswerSaveRef.current = doSave;

        if (immediate) {
            doSave();
        } else {
            answerSaveTimerRef.current = setTimeout(() => {
                answerSaveTimerRef.current = null;
                doSave();
            }, 600);
        }
    };

    // Flush any pending answer save when patient/study changes (so the
    // previous patient's typed-but-not-yet-saved answer is persisted).
    useEffect(() => {
        return () => {
            if (answerSaveTimerRef.current) {
                clearTimeout(answerSaveTimerRef.current);
                answerSaveTimerRef.current = null;
            }
            if (pendingAnswerSaveRef.current) {
                const flushed = pendingAnswerSaveRef.current;
                pendingAnswerSaveRef.current = null;
                flushed();
            }
        };
    }, [studyId, patientId]);

    // Handle question answer change - use string keys for consistency
    const handleAnswerChange = (questionId, value) => {
        const key = String(questionId);
        setQuestionAnswers(prev => {
            const next = { ...prev, [key]: value };
            // Text input: debounce so we don't post per keystroke.
            // Single-choice radio also reaches here with a single value — debouncing
            // it adds at most ~600 ms latency, which is fine.
            scheduleAnswerSave(next, scoreAnswers);
            return next;
        });
        setHasChanges(true);
    };

    // Handle multiple select checkbox change - use string keys for consistency
    const handleMultiSelectChange = (questionId, option, checked) => {
        const key = String(questionId);
        setQuestionAnswers(prev => {
            const currentAnswers = prev[key] || [];
            const next = checked
                ? { ...prev, [key]: [...currentAnswers, option] }
                : { ...prev, [key]: currentAnswers.filter(a => a !== option) };
            // Single click: save immediately.
            scheduleAnswerSave(next, scoreAnswers, true);
            return next;
        });
        setHasChanges(true);
    };

    const toggleQuestion = (questionId) => {
        setExpandedQuestion(expandedQuestion === questionId ? null : questionId);
    };

    // Check if a question has been answered - use string keys for consistency
    const isQuestionAnswered = (questionId) => {
        const key = String(questionId);
        const answer = questionAnswers[key];
        if (Array.isArray(answer)) {
            return answer.length > 0;
        }
        return answer !== undefined && answer !== null && answer !== '';
    };

    // Handle score answer change
    const handleScoreChange = (scoreId, value) => {
        const key = String(scoreId);
        setScoreAnswers(prev => {
            const next = { ...prev, [key]: value };
            // Score slider: debounce so dragging the slider doesn't post per pixel.
            scheduleAnswerSave(questionAnswers, next);
            return next;
        });
        setHasChanges(true);
    };

    // Toggle score dropdown
    const toggleScore = (scoreId) => {
        setExpandedScore(expandedScore === scoreId ? null : scoreId);
    };

    // Check if a score has been answered
    const isScoreAnswered = (scoreId) => {
        const key = String(scoreId);
        const answer = scoreAnswers[key];
        return answer !== undefined && answer !== null;
    };

    // Save annotation
    const saveAnnotation = async () => {
        if (!studyId || !patientId) {
            toast.error("Missing study or patient information");
            return;
        }

        setIsSaving(true);
        try {
            await API.post(`/studies/${studyId}/annotate/`, {
                patient_id: patientId,
                data_type: activeDataType,
                series_id: activeSeriesId,
                selected_labels: selectedLabels,
                question_answers: questionAnswers,
                score_answers: scoreAnswers
            });
            toast.success("Annotation saved successfully!");
            setHasChanges(false);

            // Reload shared annotations to show updated state
            if (shareAnnotations) {
                loadSharedAnnotations();
            }
        } catch (err) {
            console.error("Error saving annotation:", err);
            toast.error("Failed to save annotation");
        } finally {
            setIsSaving(false);
        }
    };

    // Handle request submission
    const handleRequestSubmit = async (e) => {
        if (e.key !== "Enter" || !requestInput.trim() || isProcessingRequest) return;

        setIsProcessingRequest(true);
        setRequestProgress(0);
        setRequestStatus("Processing request...");

        // Simulate progress bar (since we don't have real-time progress from backend)
        const estimatedDuration = 5000; // 5 seconds estimated
        const updateInterval = 100;
        let elapsed = 0;

        progressIntervalRef.current = setInterval(() => {
            elapsed += updateInterval;
            // Use easing function to slow down as it approaches 90%
            const progress = Math.min(90, (elapsed / estimatedDuration) * 100 * 0.9);
            setRequestProgress(progress);
        }, updateInterval);

        try {
            const response = await API.post("/studies/process-request/", {
                request: requestInput,
                study_id: studyId,
                patient_id: patientId,
                image_id: imageId
            });

            // Clear interval and complete progress
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
            }
            setRequestProgress(100);
            setRequestStatus("Request completed!");

            toast.success(response.data?.message || "Request processed successfully!");
            setRequestInput("");

            // Check if we need to refresh the viewer (e.g., after creating a rotated image)
            if (response.data?.action === "refresh_viewer") {
                setRequestStatus("Refreshing viewer...");
                // Delay refresh to show success message
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            } else {
                // Reset after brief delay
                setTimeout(() => {
                    setIsProcessingRequest(false);
                    setRequestProgress(0);
                    setRequestStatus("");
                }, 1500);
            }

        } catch (err) {
            console.error("Error processing request:", err);
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
            }
            setRequestProgress(0);
            setRequestStatus("");
            setIsProcessingRequest(false);
            toast.error(err.response?.data?.error || "Failed to process request");
        }
    };

    // Cleanup interval on unmount
    useEffect(() => {
        return () => {
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
            }
        };
    }, []);

    // Handle AI model execution started
    const handleExecutionStarted = (execution) => {
        setActiveExecutions(prev => [execution, ...prev]);
    };

    // Handle AI model execution complete
    const handleExecutionComplete = (execution) => {
        // For QA/text models, the TextResultModal in ExecutionTracker handles display
        if (execution.text_output && Object.keys(execution.text_output).length > 0) {
            return;
        }

        // Refresh the viewer when AI model execution completes successfully
        if (execution.status === 'completed') {
            console.log('AI execution completed, auto-refreshing viewer to show new segmentation');

            // Show a toast notification and auto-refresh after a short delay
            toast.success(
                <div>
                    <strong>Segmentation Ready!</strong>
                    <p style={{ margin: '5px 0 0 0', fontSize: '0.9em' }}>
                        Refreshing viewer to display the new segmentation...
                    </p>
                </div>,
                {
                    autoClose: 3000,
                    closeOnClick: false,
                }
            );

            // Auto-refresh after 2 seconds to let user see the notification
            // Preserve current patient index in the URL so we stay on the same patient
            setTimeout(() => {
                const url = new URL(window.location.href);
                // Update the current_index parameter to preserve patient position
                url.searchParams.set('current_index', currentPatientIndex.toString());
                // Also update patient_id to ensure the correct patient is used
                if (patientId) {
                    url.searchParams.set('patient_id', patientId);
                }
                window.location.href = url.toString();
            }, 2000);
        }
    };

    // Handle tool execution submission (runs Docker container via AI model execution API)
    const handleSubmitToolTask = async () => {
        if (!selectedSignalId) {
            toast.error('Please select a signal');
            return;
        }

        if (!selectedTool) {
            toast.error('No tool selected');
            return;
        }

        setIsSubmittingHRTask(true);

        try {
            const selectedRecord = signalRecords.find(r => String(r.id) === String(selectedSignalId));
            const selectedChannel = selectedRecord?.metadata?.channels?.[parseInt(selectedChannelIndex)];
            const toolName = selectedTool.name?.split('/').pop() || selectedTool.name || 'Tool';

            // Build parameters: merge common signal params with tool-specific params
            const parameters = {
                signal_id: parseInt(selectedSignalId),
                signal_filename: selectedRecord?.filename || '',
                sample_rate: parseFloat(manualSamplingFreq) || selectedChannel?.sample_rate || null,
                sampling_frequency: parseFloat(manualSamplingFreq) || selectedChannel?.sample_rate || 0,
                ...toolParams, // Tool-specific parameters from parameters_schema
            };

            // Only include channel_index if the tool doesn't define its own channel params
            const schema = selectedTool.parameters_schema || {};
            const hasChannelParams = Object.keys(schema).some(k => k.startsWith('channel_'));
            if (!hasChannelParams && selectedChannelIndex) {
                parameters.channel_index = parseInt(selectedChannelIndex);
                parameters.channel_label = selectedChannel?.label || `Channel ${selectedChannelIndex}`;
            }

            // Call the AI model execution API — this runs the Docker container
            const response = await API.post(`/ai-models/models/${selectedTool.id}/run/`, {
                patient_id: patientId,
                study_id: studyId,
                parameters,
            });

            const executionId = response.data.id;

            // Add to tool executions tracking
            const newExecution = {
                id: executionId.toString(),
                executionId: executionId,
                toolName: toolName,
                toolIcon: selectedTool.icon || 'fa-wrench',
                status: 'running',
                progress: 0,
                startTime: new Date(),
                config: {
                    signalName: selectedRecord?.filename || `Signal ${selectedSignalId}`,
                    channelName: selectedChannel?.label || `Channel ${selectedChannelIndex}`,
                    sampleRate: selectedChannel?.sample_rate || 'N/A'
                }
            };

            setToolExecutions(prev => [newExecution, ...prev]);

            toast.success(
                <div>
                    <strong>{toolName} Started!</strong>
                    <p style={{ margin: '5px 0 0 0', fontSize: '0.9em' }}>
                        Check the Tools History dropdown to track progress.
                    </p>
                </div>,
                { autoClose: 3000 }
            );

            setShowToolConfig(false);
            setShowToolsDropdown(false);
            setSelectedSignalId('');
            setSelectedChannelIndex('');
            setManualSamplingFreq('');
            setToolParams({});

            // Poll for execution completion via AI model execution API
            const pollInterval = setInterval(async () => {
                try {
                    const statusResponse = await API.get(`/ai-models/executions/${executionId}/`);
                    const execution = statusResponse.data;
                    const execStatus = execution.status;

                    // Update execution status
                    setToolExecutions(prev => prev.map(exec => {
                        if (exec.id === executionId.toString()) {
                            if (execStatus === 'running' || execStatus === 'preparing') {
                                return { ...exec, status: 'running', progress: execution.progress || 50 };
                            } else if (execStatus === 'completed') {
                                return {
                                    ...exec,
                                    status: 'completed',
                                    progress: 100,
                                    endTime: new Date(),
                                    result: execution
                                };
                            } else if (execStatus === 'failed') {
                                return {
                                    ...exec,
                                    status: 'failed',
                                    progress: 0,
                                    endTime: new Date(),
                                    error: execution.error_message || 'Unknown error'
                                };
                            }
                        }
                        return exec;
                    }));

                    if (execStatus === 'completed') {
                        clearInterval(pollInterval);

                        // Check if output created new signal(s) (PatientData)
                        const outputIds = execution.output_patient_data_ids || [];
                        if (outputIds.length === 0 && execution.output_patient_data) {
                            // Fallback for single output
                            outputIds.push(execution.output_patient_data);
                        }
                        if (outputIds.length > 0) {
                            const url = new URL(window.location.href);
                            const currentSignalData = url.searchParams.get('signal_data') || '';
                            const signalIds = currentSignalData.split(',').filter(id => id);
                            for (const outId of outputIds) {
                                signalIds.push(`${outId}:PatientData`);
                            }
                            url.searchParams.set('signal_data', signalIds.join(','));
                            // Preserve current patient index so we stay on the same patient
                            url.searchParams.set('current_index', currentPatientIndex.toString());

                            toast.success(`${toolName} completed! Refreshing viewer with ${outputIds.length} output(s)...`);
                            setTimeout(() => {
                                window.location.href = url.toString();
                            }, 1000);
                        } else {
                            toast.success(`${toolName} completed!`);
                        }
                    } else if (execStatus === 'failed') {
                        clearInterval(pollInterval);
                        toast.error(`${toolName} failed: ${execution.error_message || 'Unknown error'}`);
                    }
                } catch (err) {
                    console.error('Error polling execution status:', err);
                }
            }, 3000); // Poll every 3 seconds

            // Safety timeout after 10 minutes (tools may take longer than the old 60s)
            setTimeout(() => {
                clearInterval(pollInterval);
            }, 600000);

        } catch (err) {
            console.error('Error submitting tool task:', err);
            toast.error(err.response?.data?.error || 'Failed to submit tool task');
        } finally {
            setIsSubmittingHRTask(false);
        }
    };

    return (
        <>
        <nav className="viewer-navbar">
            <div className="viewer-navbar-left">
                {/* Logo */}
                <img
                    src={logoSrc}
                    alt="Logo"
                    className="viewer-logo"
                    onClick={async () => { if (onBeforeLeave) await onBeforeLeave(); navigate("/Dashboard"); }}
                    title="Go to Dashboard"
                />

                {/* Back to Study */}
                {studyId && (
                    <button
                        className="viewer-nav-btn"
                        onClick={async () => { if (onBeforeLeave) await onBeforeLeave(); navigate(`/Study/${studyId}`); }}
                        title="Back to Study"
                    >
                        <i className="fa fa-arrow-left"></i>
                        <span>Back to Study</span>
                    </button>
                )}

                {/* Trame-only: flip vertical for arthrex-style 2D stacks whose
                    raw pixel data is anatomically inverted. */}
                {onToggleTrameOrientation && (
                    <button
                        className="viewer-nav-btn"
                        onClick={onToggleTrameOrientation}
                        title="Flip the volume vertically (for patients whose image appears upside-down)"
                    >
                        <i className="fa fa-arrows-alt-v"></i>
                        <span>Flip Vertical</span>
                    </button>
                )}

                {/* Study Name */}
                {studyName && (
                    <>
                        <div className="viewer-navbar-divider"></div>
                        <div className="viewer-study-info">
                            <i className="fa fa-book"></i>
                            <span>{studyName}</span>
                        </div>
                    </>
                )}

                {/* Patient Navigation - shown when multiple patients are selected */}
                {patientIds.length > 1 && onPatientChange && (
                    <>
                        <div className="viewer-navbar-divider"></div>
                        <div className="patient-navigation">
                            <button
                                className="patient-nav-btn"
                                onClick={() => onPatientChange(currentPatientIndex - 1)}
                                disabled={currentPatientIndex === 0 || isNavigating}
                                title="Previous Patient"
                            >
                                <i className="fa fa-chevron-left"></i>
                            </button>
                            <div className="patient-nav-info">
                                {isNavigating ? (
                                    <>
                                        <i className="fa fa-spinner fa-spin"></i>
                                        <span className="patient-nav-wait">Please wait...</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="fa fa-user"></i>
                                        <span className="patient-nav-current">{patientId}</span>
                                        <span className="patient-nav-count">
                                            ({currentPatientIndex + 1}/{patientIds.length})
                                        </span>
                                    </>
                                )}
                            </div>
                            <button
                                className="patient-nav-btn"
                                onClick={() => onPatientChange(currentPatientIndex + 1)}
                                disabled={currentPatientIndex === patientIds.length - 1 || isNavigating}
                                title="Next Patient"
                            >
                                <i className="fa fa-chevron-right"></i>
                            </button>
                        </div>
                    </>
                )}

                {/* Reader-queue navigation — opt-in per study (Viewer Navbar
                    config). Walks the reviewer's randomized queue, marks the
                    current case complete on Next, and resolves the next
                    patient via /my-queue/ so admin-driven queue changes show
                    up immediately. */}
                {queueNavEnabled && (queueLoading || queueLength > 0) && (
                    <>
                        <div className="viewer-navbar-divider"></div>
                        <div className="patient-navigation">
                            <button
                                className="patient-nav-btn"
                                onClick={handleQueuePrev}
                                disabled={queueAtStart || queueNavigating || queueLoading}
                                title="Previous case in your queue"
                            >
                                <i className="fa fa-chevron-left"></i>
                            </button>
                            <div className="patient-nav-info">
                                {queueLoading ? (
                                    <>
                                        <i className="fa fa-spinner fa-spin"></i>
                                        <span className="patient-nav-wait">Loading queue…</span>
                                    </>
                                ) : queueNavigating ? (
                                    <>
                                        <i className="fa fa-spinner fa-spin"></i>
                                        <span className="patient-nav-wait">Advancing…</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="fa fa-list-ol"></i>
                                        <span className="patient-nav-count">
                                            Case {queueIndex >= 0 ? queueIndex + 1 : '–'} / {queueLength}
                                        </span>
                                    </>
                                )}
                            </div>
                            <button
                                className="patient-nav-btn"
                                onClick={handleQueueNext}
                                disabled={queueNavigating || queueLoading}
                                title={queueAtEnd ? 'Finish' : 'Next case in your queue'}
                            >
                                <i className={`fa ${queueAtEnd ? 'fa-check' : 'fa-chevron-right'}`}></i>
                            </button>
                        </div>
                    </>
                )}
            </div>

            <div className="viewer-navbar-center">
                {/* Annotation Level Selector - shown when there are multiple annotation levels configured */}
                {availableAnnotationLevels.length > 1 && (
                    <div className="data-context-selector">
                        <span className="context-label">
                            <i className="fa fa-layer-group"></i>
                            Level:
                        </span>
                        <select
                            className="context-select level-select"
                            value={selectedAnnotationLevel}
                            onChange={(e) => handleAnnotationLevelChange(e.target.value)}
                        >
                            {availableAnnotationLevels.includes('patient') && (
                                <option value="patient">Patient</option>
                            )}
                            {availableAnnotationLevels.includes('data_type') && (
                                <option value="data_type">Data Type</option>
                            )}
                            {availableAnnotationLevels.includes('series') && (
                                <option value="series">Series</option>
                            )}
                        </select>

                        {/* Data Type Selector - shown when data_type or series level is selected */}
                        {(selectedAnnotationLevel === 'data_type' || selectedAnnotationLevel === 'series') && (
                            <select
                                className="context-select data-type-select"
                                value={activeDataType}
                                onChange={(e) => handleDataTypeChange(e.target.value)}
                            >
                                <option value="">Select Data Type</option>
                                {effectiveAvailableDataTypes.map(dt => (
                                    <option key={dt} value={dt}>{dt.charAt(0).toUpperCase() + dt.slice(1)}</option>
                                ))}
                            </select>
                        )}

                        {/* Series Selector - shown when series level is selected and data type is chosen */}
                        {selectedAnnotationLevel === 'series' && activeDataType && (() => {
                            const filteredSeries = availableSeries.filter(s => s.dataType === activeDataType);
                            return filteredSeries.length > 0 ? (
                                <select
                                    className="context-select series-select"
                                    value={activeSeriesId}
                                    onChange={(e) => handleSeriesChange(e.target.value)}
                                >
                                    <option value="">Select Series</option>
                                    {filteredSeries.map(s => (
                                        <option key={`series-${s.id}`} value={s.id}>{s.name || s.id}</option>
                                    ))}
                                </select>
                            ) : (
                                <span className="context-info">
                                    <i className="fa fa-info-circle"></i>
                                    <span>No {activeDataType} series available</span>
                                </span>
                            );
                        })()}

                        {/* Warning if selection is incomplete (except for series with no available series) */}
                        {!isLevelSelectionComplete() && !(selectedAnnotationLevel === 'series' && activeDataType && availableSeries.length === 0) && (
                            <span className="context-warning">
                                <i className="fa fa-exclamation-triangle"></i>
                            </span>
                        )}
                    </div>
                )}

                {/* Annotation Tools Container */}
                {(filteredLabels.length > 0 || filteredQuestions.length > 0 || filteredScores.length > 0) && (
                    <div className="annotation-tools-container">
                        {/* Labels Section */}
                        {filteredLabels.length > 0 && labelDisplayMode !== 'side_panel' && (
                            <div className="annotation-section labels-section">
                                <div className="labels-dropdown-container" ref={labelsDropdownRef}>
                                    <button
                                        className={`labels-dropdown-toggle ${showLabelsDropdown ? 'active' : ''} ${selectedLabels.length > 0 ? 'has-selection' : ''}`}
                                        onClick={() => setShowLabelsDropdown(!showLabelsDropdown)}
                                    >
                                        <i className="fa fa-tags"></i>
                                        <span>Labels</span>
                                        <span className="labels-count-badge">{selectedLabels.length}/{filteredLabels.length}</span>
                                        <i className={`fa fa-chevron-${showLabelsDropdown ? 'up' : 'down'} dropdown-chevron`}></i>
                                    </button>

                                    {/* Selected Labels Preview */}
                                    {selectedLabels.length > 0 && !showLabelsDropdown && (
                                        <div className="selected-labels-preview">
                                            {selectedLabels.slice(0, 3).map((label) => (
                                                <span
                                                    key={label.id}
                                                    className="selected-label-chip"
                                                    style={{ backgroundColor: label.color }}
                                                    title={label.name}
                                                >
                                                    {label.name.length > 10 ? label.name.substring(0, 10) + '...' : label.name}
                                                </span>
                                            ))}
                                            {selectedLabels.length > 3 && (
                                                <span className="more-labels-badge">+{selectedLabels.length - 3}</span>
                                            )}
                                        </div>
                                    )}

                                    {/* Labels Dropdown Panel */}
                                    {showLabelsDropdown && (
                                        <div className="labels-dropdown-panel" onClick={(e) => e.stopPropagation()}>
                                            <div className="labels-dropdown-header">
                                                <span>Select Labels</span>
                                                {isSingleSelectionMode && (
                                                    <span className="single-select-hint">Single selection</span>
                                                )}
                                            </div>
                                            <div className="labels-dropdown-list">
                                                {filteredLabels.map((label) => (
                                                    <button
                                                        key={label.id}
                                                        className={`label-dropdown-item ${isLabelSelected(label) ? "selected" : ""}`}
                                                        onClick={() => handleToggleLabel(label)}
                                                    >
                                                        <span
                                                            className="label-color-dot"
                                                            style={{ backgroundColor: label.color }}
                                                        ></span>
                                                        <span className="label-item-name">{label.name}</span>
                                                        {label.shortcut && (
                                                            <span className="label-item-shortcut">{label.shortcut}</span>
                                                        )}
                                                        {isLabelSelected(label) && (
                                                            <i className="fa fa-check label-selected-check"></i>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Labels indicator in navbar when side panel mode is active */}
                        {filteredLabels.length > 0 && labelDisplayMode === 'side_panel' && (
                            <div className="annotation-section labels-section">
                                <button
                                    className={`labels-dropdown-toggle ${selectedLabels.length > 0 ? 'has-selection' : ''}`}
                                    onClick={() => setLabelsPanelOpen(prev => !prev)}
                                    title={labelsPanelOpen ? 'Collapse labels panel' : 'Expand labels panel'}
                                >
                                    <i className="fa fa-tags"></i>
                                    <span>Labels</span>
                                    <span className="labels-count-badge">{selectedLabels.length}/{filteredLabels.length}</span>
                                    <i className={`fa fa-chevron-${labelsPanelOpen ? 'right' : 'left'} dropdown-chevron`}></i>
                                </button>
                            </div>
                        )}

                        {/* Questions Section */}
                        {filteredQuestions.length > 0 && (
                            <div className="annotation-section questions-section">
                                <div className="section-header">
                                    <i className="fa fa-question-circle"></i>
                                    <span>Questions</span>
                                    <span className="section-count">
                                        {filteredQuestions.filter(q => isQuestionAnswered(q.id)).length}/{filteredQuestions.length}
                                    </span>
                                </div>
                                <div className="viewer-questions-bar">
                                    {filteredQuestions.map((q, index) => {
                                        // Two schemas exist in the wild:
                                        //   canonical:  { question, type: "text" | "multiple_choice" | "multiple_select" }
                                        //   legacy:     { text,     type: "text" | "select"          | "multiselect"      }
                                        // Normalize at render time so legacy questions still display.
                                        const qText = q.question || q.text || "";
                                        const qType = q.type === "select" ? "multiple_choice"
                                                    : q.type === "multiselect" ? "multiple_select"
                                                    : q.type;
                                        return (
                                        <div key={q.id} className="question-dropdown-container">
                                            <button
                                                className={`question-toggle-btn ${expandedQuestion === q.id ? "active" : ""} ${q.required ? "required" : ""} ${isQuestionAnswered(q.id) ? "answered" : ""}`}
                                                onClick={() => toggleQuestion(q.id)}
                                                title={qText}
                                            >
                                                Q{index + 1}
                                                {isQuestionAnswered(q.id) && <i className="fa fa-check q-answered-check"></i>}
                                                {q.required && !isQuestionAnswered(q.id) && <span className="q-required-dot"></span>}
                                                <i className={`fa fa-chevron-${expandedQuestion === q.id ? "up" : "down"}`}></i>
                                            </button>

                                            {/* Expanded Question Panel */}
                                            {expandedQuestion === q.id && (
                                                <div
                                                    className="question-dropdown-panel"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <div className="question-dropdown-header">
                                                        <span className="question-full-text">{qText}</span>
                                                        {q.required && <span className="required-badge">Required</span>}
                                                    </div>
                                                    <div className="question-dropdown-body">
                                                        {qType === "text" ? (
                                                            <textarea
                                                                className="question-text-input"
                                                                placeholder="Enter your answer..."
                                                                rows={2}
                                                                value={questionAnswers[String(q.id)] || ''}
                                                                onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                                                            />
                                                        ) : qType === "multiple_choice" ? (
                                                            <div className="question-options">
                                                                {q.options?.map((option, optIndex) => {
                                                                    const currentAnswer = questionAnswers[String(q.id)];
                                                                    const isChecked = currentAnswer === option;
                                                                    return (
                                                                        <label
                                                                            key={optIndex}
                                                                            className={`option-radio ${isChecked ? 'selected' : ''}`}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        >
                                                                            <input
                                                                                type="radio"
                                                                                name={`question-${q.id}`}
                                                                                checked={isChecked}
                                                                                onChange={() => handleAnswerChange(q.id, option)}
                                                                            />
                                                                            <span>{option}</span>
                                                                        </label>
                                                                    );
                                                                })}
                                                            </div>
                                                        ) : qType === "multiple_select" ? (
                                                            <div className="question-options">
                                                                {q.options?.map((option, optIndex) => {
                                                                    const currentAnswers = questionAnswers[String(q.id)] || [];
                                                                    const isChecked = currentAnswers.includes(option);
                                                                    return (
                                                                        <label
                                                                            key={optIndex}
                                                                            className={`option-checkbox ${isChecked ? 'selected' : ''}`}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        >
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={isChecked}
                                                                                onChange={(e) => handleMultiSelectChange(q.id, option, e.target.checked)}
                                                                            />
                                                                            <span>{option}</span>
                                                                        </label>
                                                                    );
                                                                })}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Scores Section */}
                        {filteredScores.length > 0 && (
                            <div className="annotation-section scores-section">
                                <div className="section-header">
                                    <i className="fa fa-star"></i>
                                    <span>Scores</span>
                                    <span className="section-count">
                                        {filteredScores.filter(s => isScoreAnswered(s.id)).length}/{filteredScores.length}
                                    </span>
                                </div>
                                <div className="viewer-scores-bar">
                                    {filteredScores.map((score, index) => (
                                        <div key={score.id} className="score-dropdown-container">
                                            <button
                                                className={`score-toggle-btn ${expandedScore === score.id ? "active" : ""} ${score.required ? "required" : ""} ${isScoreAnswered(score.id) ? "answered" : ""}`}
                                                onClick={() => toggleScore(score.id)}
                                                title={score.name}
                                            >
                                                S{index + 1}
                                                {isScoreAnswered(score.id) && (
                                                    <span className="score-value-badge">
                                                        {scoreAnswers[String(score.id)]}
                                                    </span>
                                                )}
                                                {score.required && !isScoreAnswered(score.id) && <span className="q-required-dot"></span>}
                                                <i className={`fa fa-chevron-${expandedScore === score.id ? "up" : "down"}`}></i>
                                            </button>

                                            {/* Expanded Score Panel */}
                                            {expandedScore === score.id && (
                                                <div
                                                    className="score-dropdown-panel"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <div className="score-dropdown-header">
                                                        <span className="score-name">{score.name}</span>
                                                        {score.required && <span className="required-badge">Required</span>}
                                                    </div>
                                                    {score.description && (
                                                        <div className="score-description">{score.description}</div>
                                                    )}
                                                    <div className="score-dropdown-body">
                                                        <div className="score-slider-container">
                                                            <span className="score-min">{score.min}</span>
                                                            <input
                                                                type="range"
                                                                className="score-slider"
                                                                min={score.min}
                                                                max={score.max}
                                                                value={scoreAnswers[String(score.id)] ?? score.min}
                                                                onChange={(e) => handleScoreChange(score.id, parseInt(e.target.value))}
                                                            />
                                                            <span className="score-max">{score.max}</span>
                                                        </div>
                                                        <div className="score-current-value">
                                                            {scoreAnswers[String(score.id)] !== undefined ? (
                                                                <span className="score-value">{scoreAnswers[String(score.id)]}</span>
                                                            ) : (
                                                                <span className="score-placeholder">Select a score</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                    </div>
                )}

                {/* Shared Annotations Button & Panel */}
                {shareAnnotations && otherUsersAnnotations.length > 0 && (
                    <div className="shared-annotations-container">
                        <button
                            className={`viewer-nav-btn shared-annotations-btn ${showSharedAnnotations ? 'active' : ''}`}
                            onClick={() => setShowSharedAnnotations(!showSharedAnnotations)}
                            title="View team annotations"
                        >
                            <i className="fa fa-users"></i>
                            <span>Team ({otherUsersAnnotations.length})</span>
                            <i className={`fa fa-chevron-${showSharedAnnotations ? 'up' : 'down'}`}></i>
                        </button>

                        {showSharedAnnotations && (
                            <div className="shared-annotations-panel">
                                <div className="shared-annotations-header">
                                    <i className="fa fa-users"></i>
                                    <span>Team Annotations</span>
                                </div>
                                <div className="shared-annotations-list">
                                    {otherUsersAnnotations.map((annotation, index) => (
                                        <div key={annotation.id || index} className="shared-annotation-item">
                                            <div className="annotation-user-info">
                                                <i className="fa fa-user-circle"></i>
                                                <span className="annotation-username">
                                                    {annotation.annotated_by_user?.username || 'Unknown User'}
                                                </span>
                                                <span className="annotation-time">
                                                    {new Date(annotation.updated_at).toLocaleDateString()}
                                                </span>
                                            </div>

                                            {/* Labels from this user */}
                                            {annotation.selected_labels && annotation.selected_labels.length > 0 && (
                                                <div className="annotation-labels">
                                                    <span className="annotation-section-label">Labels:</span>
                                                    <div className="annotation-label-chips">
                                                        {annotation.selected_labels.map((label, labelIndex) => (
                                                            <span
                                                                key={labelIndex}
                                                                className="annotation-label-chip"
                                                                style={{ backgroundColor: label.color, color: 'white' }}
                                                            >
                                                                {label.name}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Question answers from this user */}
                                            {annotation.question_answers && Object.keys(annotation.question_answers).length > 0 && (
                                                <div className="annotation-answers">
                                                    <span className="annotation-section-label">Answers:</span>
                                                    <div className="annotation-answers-list">
                                                        {questions.map((q, qIndex) => {
                                                            const answer = annotation.question_answers[String(q.id)];
                                                            if (!answer || (Array.isArray(answer) && answer.length === 0)) return null;
                                                            return (
                                                                <div key={qIndex} className="annotation-answer-item">
                                                                    <span className="answer-question">Q{qIndex + 1}:</span>
                                                                    <span className="answer-value">
                                                                        {Array.isArray(answer) ? answer.join(', ') : answer}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="viewer-navbar-right">
                {/* Load Annotations Section */}
                {patientId && navbarShows('annotations') && (
                    <div className="annotations-loader-container" ref={annotationsDropdownRef}>
                        <button
                            className={`viewer-nav-btn annotations-btn ${showAnnotationsDropdown ? 'active' : ''}`}
                            onClick={() => setShowAnnotationsDropdown(!showAnnotationsDropdown)}
                            title="Load Annotations"
                        >
                            <i className="fa fa-layer-group"></i>
                            <span>Annotations</span>
                            <i className={`fa fa-chevron-${showAnnotationsDropdown ? 'up' : 'down'}`}></i>
                        </button>

                        {showAnnotationsDropdown && (
                            <div className="annotations-dropdown-panel" onClick={(e) => e.stopPropagation()}>
                                <div className="annotations-dropdown-header">
                                    <i className="fa fa-layer-group"></i>
                                    <span>Load Annotations</span>
                                </div>

                                <div className="annotations-dropdown-body">
                                    {isFetchingAnnotations ? (
                                        <div className="annotations-loading">
                                            <i className="fa fa-spinner fa-spin"></i>
                                            <span>Loading annotations...</span>
                                        </div>
                                    ) : availableAnnotations.length === 0 ? (
                                        <div className="annotations-empty">
                                            <i className="fa fa-info-circle"></i>
                                            <span>No annotations available for this patient</span>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="annotations-list">
                                                {availableAnnotations.map((ann) => (
                                                    <label
                                                        key={ann.id}
                                                        className={`annotation-item ${selectedAnnotationIds.includes(ann.id) ? 'selected' : ''} ${ann.already_in_orthanc ? 'already-loaded' : ''}`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedAnnotationIds.includes(ann.id)}
                                                            onChange={() => toggleAnnotationSelection(ann.id)}
                                                        />
                                                        <div className="annotation-item-info">
                                                            <span className="annotation-filename">{ann.filename}</span>
                                                            {ann.description && (
                                                                <span className="annotation-description">{ann.description}</span>
                                                            )}
                                                            <div className="annotation-meta">
                                                                <span className="annotation-type">{ann.original_file_type}</span>
                                                                {ann.deleted_from_viewer ? (
                                                                    <span className="annotation-status deleted">
                                                                        <i className="fa fa-trash"></i> Removed from viewer
                                                                    </span>
                                                                ) : ann.already_in_orthanc ? (
                                                                    <span className="annotation-status loaded">
                                                                        <i className="fa fa-check"></i> Loaded
                                                                    </span>
                                                                ) : ann.already_converted ? (
                                                                    <span className="annotation-status converted">
                                                                        <i className="fa fa-exchange-alt"></i> Converted
                                                                    </span>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>

                                            <div className="annotations-dropdown-footer">
                                                <button
                                                    className="load-annotations-btn"
                                                    onClick={handleLoadAnnotations}
                                                    disabled={selectedAnnotationIds.length === 0 || isLoadingAnnotations}
                                                >
                                                    {isLoadingAnnotations ? (
                                                        <>
                                                            <i className="fa fa-spinner fa-spin"></i>
                                                            <span>Loading...</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fa fa-upload"></i>
                                                            <span>Load Selected ({selectedAnnotationIds.length})</span>
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Load Maps Section */}
                {patientId && navbarShows('maps') && (
                    <div className="maps-loader-container" ref={mapsDropdownRef}>
                        <button
                            className={`viewer-nav-btn maps-btn ${showMapsDropdown ? 'active' : ''}`}
                            onClick={() => setShowMapsDropdown(!showMapsDropdown)}
                            title="Load Maps"
                        >
                            <i className="fa fa-map"></i>
                            <span>Maps</span>
                            <i className={`fa fa-chevron-${showMapsDropdown ? 'up' : 'down'}`}></i>
                        </button>

                        {showMapsDropdown && (
                            <div className="maps-dropdown-panel" onClick={(e) => e.stopPropagation()}>
                                <div className="maps-dropdown-header">
                                    <i className="fa fa-map"></i>
                                    <span>Load Maps</span>
                                </div>

                                <div className="maps-dropdown-body">
                                    {isFetchingMaps ? (
                                        <div className="maps-loading">
                                            <i className="fa fa-spinner fa-spin"></i>
                                            <span>Loading maps...</span>
                                        </div>
                                    ) : availableMaps.length === 0 ? (
                                        <div className="maps-empty">
                                            <i className="fa fa-info-circle"></i>
                                            <span>No maps available for this patient</span>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="maps-list">
                                                {availableMaps.map((m) => (
                                                    <label
                                                        key={m.id}
                                                        className={`map-item ${selectedMapIds.includes(m.id) ? 'selected' : ''} ${m.already_in_orthanc ? 'already-loaded' : ''}`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedMapIds.includes(m.id)}
                                                            onChange={() => toggleMapSelection(m.id)}
                                                            disabled={m.already_in_orthanc}
                                                        />
                                                        <div className="map-item-info">
                                                            <span className="map-filename">{m.filename}</span>
                                                            {m.description && (
                                                                <span className="map-description">{m.description}</span>
                                                            )}
                                                            <div className="map-meta">
                                                                <span className="map-type">{m.original_file_type}</span>
                                                                {m.already_in_orthanc && (
                                                                    <span className="map-status loaded">
                                                                        <i className="fa fa-check"></i> Loaded
                                                                    </span>
                                                                )}
                                                                {m.already_converted && !m.already_in_orthanc && (
                                                                    <span className="map-status converted">
                                                                        <i className="fa fa-exchange-alt"></i> Converted
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>

                                            <div className="maps-dropdown-footer">
                                                <button
                                                    className="load-maps-btn"
                                                    onClick={handleLoadMaps}
                                                    disabled={selectedMapIds.length === 0 || isLoadingMaps}
                                                >
                                                    {isLoadingMaps ? (
                                                        <>
                                                            <i className="fa fa-spinner fa-spin"></i>
                                                            <span>Loading...</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fa fa-upload"></i>
                                                            <span>Load Selected ({selectedMapIds.length})</span>
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* AI Models Section */}
                {navbarShows('ai_models') && studyId && (
                    <div className="ai-models-section">
                        <AIModelSelector
                            studyId={studyId}
                            patientId={patientId}
                            imageId={imageId}
                            onExecutionStarted={handleExecutionStarted}
                        />
                        <ExecutionTracker
                            patientId={patientId}
                            studyId={studyId}
                            executions={activeExecutions}
                            onExecutionComplete={handleExecutionComplete}
                        />
                    </div>
                )}

                {/* Tools Section - Dynamic tools from study assignments */}
                {navbarShows('tools') && (showTools || assignedTools.length > 0) && (
                    <div className="tools-section" ref={toolsDropdownRef}>
                        <button
                            className={`viewer-nav-btn tools-btn ${showToolsDropdown ? 'active' : ''}`}
                            onClick={() => setShowToolsDropdown(!showToolsDropdown)}
                            title="Processing Tools"
                        >
                            <i className="fa fa-wrench"></i>
                            <span>Tools</span>
                            <i className={`fa fa-chevron-${showToolsDropdown ? 'up' : 'down'}`}></i>
                        </button>

                        {showToolsDropdown && (
                            <div className="tools-dropdown-panel" onClick={(e) => e.stopPropagation()}>
                                <div className="tools-dropdown-header">
                                    <i className="fa fa-wrench"></i>
                                    <span>Processing Tools</span>
                                </div>

                                <div className="tools-dropdown-body">
                                    {loadingAssignedTools && (
                                        <div className="text-center py-2">
                                            <i className="fa fa-spinner fa-spin"></i>
                                        </div>
                                    )}

                                    {/* Dynamically assigned tools from study */}
                                    {assignedTools.map((tool) => (
                                        <button
                                            key={tool.id}
                                            className="tool-item"
                                            onClick={() => {
                                                // For signal tools with matching input type, show config modal
                                                if (tool.model_type?.startsWith('tool_') && showTools && signalRecords.length > 0) {
                                                    setSelectedTool(tool);
                                                    // Initialize dynamic params from tool's parameters_schema defaults
                                                    const defaults = {};
                                                    if (tool.parameters_schema) {
                                                        Object.entries(tool.parameters_schema).forEach(([key, config]) => {
                                                            if (config.default !== undefined) {
                                                                defaults[key] = config.default;
                                                            }
                                                        });
                                                    }
                                                    setToolParams(defaults);
                                                    setShowToolConfig(true);
                                                    setShowToolsDropdown(false);
                                                } else {
                                                    toast.info(`Tool "${tool.name}" execution coming soon`);
                                                    setShowToolsDropdown(false);
                                                }
                                            }}
                                        >
                                            <i className={`fa ${tool.icon || 'fa-wrench'}`} style={{ color: tool.color || '#6c757d' }}></i>
                                            <div className="tool-item-info">
                                                <span className="tool-name">{tool.name}</span>
                                                <span className="tool-description">{tool.description || tool.model_type_display || 'Processing tool'}</span>
                                            </div>
                                        </button>
                                    ))}

                                    {/* Show empty state if no tools assigned and no signal records */}
                                    {assignedTools.length === 0 && !(showTools && signalRecords.length > 0) && !loadingAssignedTools && (
                                        <div className="text-center py-2" style={{ color: '#8b949e', fontSize: '0.85rem' }}>
                                            No tools assigned to this study
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Signal Tool Configuration Modal (dynamic from parameters_schema) */}
                        {showToolConfig && selectedTool && (
                            <div className="modal-backdrop" style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'rgba(0, 0, 0, 0.5)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 1060
                            }}>
                                <div className="modal-dialog" style={{ maxWidth: '500px', width: '90%', maxHeight: '80vh', overflow: 'auto' }}>
                                    <div className="modal-content" style={{ background: '#1c2128', border: '1px solid #30363d', borderRadius: '12px' }}>
                                        <div className="modal-header" style={{ borderBottom: '1px solid #30363d', padding: '1rem 1.5rem' }}>
                                            <h5 className="modal-title" style={{ color: '#58a6ff', margin: 0, fontWeight: 600 }}>
                                                <i className={`fa ${selectedTool?.icon || 'fa-wrench'} me-2`} style={{ color: selectedTool?.color || '#58a6ff' }}></i>
                                                {selectedTool?.name?.split('/').pop() || 'Run Tool'}
                                            </h5>
                                            <button
                                                type="button"
                                                onClick={() => setShowToolConfig(false)}
                                                style={{ background: 'transparent', border: 'none', color: '#8b949e', fontSize: '1.5rem', cursor: 'pointer' }}
                                            >
                                                <i className="fa fa-times"></i>
                                            </button>
                                        </div>
                                        <div className="modal-body" style={{ padding: '1.5rem' }}>
                                            <p style={{ color: '#8b949e', fontSize: '0.9rem', marginBottom: '1rem' }}>
                                                {selectedTool?.description || 'Configure and run this tool on the selected signal data.'}
                                            </p>

                                            {/* Signal Record selector */}
                                            <div style={{ marginBottom: '1rem' }}>
                                                <label className="form-label" style={{ color: '#e6edf3', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'block' }}>
                                                    Signal Record
                                                </label>
                                                <select
                                                    className="form-select"
                                                    value={selectedSignalId}
                                                    onChange={(e) => {
                                                        setSelectedSignalId(e.target.value);
                                                        setSelectedChannelIndex('');
                                                        // Auto-fill sampling frequency from first channel
                                                        const record = signalRecords.find(r => String(r.id) === String(e.target.value));
                                                        const ch = record?.metadata?.channels?.[0];
                                                        if (ch?.sample_rate) {
                                                            setManualSamplingFreq(String(ch.sample_rate));
                                                        }
                                                    }}
                                                    style={{
                                                        background: '#21262d',
                                                        border: '1px solid #30363d',
                                                        color: '#e6edf3',
                                                        padding: '0.5rem',
                                                        borderRadius: '6px',
                                                        width: '100%'
                                                    }}
                                                >
                                                    <option value="">Select a signal...</option>
                                                    {signalRecords.map(record => (
                                                        <option key={record.id} value={record.id}>
                                                            {record.filename || `Signal ${record.id}`}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Channel selector — only show if tool has NO channel_* params in schema */}
                                            {(() => {
                                                const schema = selectedTool?.parameters_schema || {};
                                                const hasChannelParams = Object.keys(schema).some(k => k.startsWith('channel_'));
                                                if (hasChannelParams) return null;
                                                return (
                                                    <div style={{ marginBottom: '1rem' }}>
                                                        <label className="form-label" style={{ color: '#e6edf3', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'block' }}>
                                                            Channel
                                                        </label>
                                                        <select
                                                            className="form-select"
                                                            value={selectedChannelIndex}
                                                            onChange={(e) => {
                                                                setSelectedChannelIndex(e.target.value);
                                                                const record = signalRecords.find(r => String(r.id) === String(selectedSignalId));
                                                                const ch = record?.metadata?.channels?.[parseInt(e.target.value)];
                                                                if (ch?.sample_rate) {
                                                                    setManualSamplingFreq(String(ch.sample_rate));
                                                                }
                                                            }}
                                                            disabled={!selectedSignalId}
                                                            style={{
                                                                background: '#21262d',
                                                                border: '1px solid #30363d',
                                                                color: '#e6edf3',
                                                                padding: '0.5rem',
                                                                borderRadius: '6px',
                                                                width: '100%'
                                                            }}
                                                        >
                                                            <option value="">Select a channel...</option>
                                                            {selectedSignalId && (() => {
                                                                const record = signalRecords.find(r => String(r.id) === String(selectedSignalId));
                                                                if (!record?.metadata?.channels) return null;
                                                                return record.metadata.channels.map(ch => (
                                                                    <option key={ch.index} value={ch.index}>
                                                                        {ch.label || `Channel ${ch.index}`} ({ch.sample_rate} Hz)
                                                                    </option>
                                                                ));
                                                            })()}
                                                        </select>
                                                    </div>
                                                );
                                            })()}

                                            {/* Dynamic parameters from tool's parameters_schema */}
                                            {selectedTool?.parameters_schema && Object.entries(selectedTool.parameters_schema).map(([key, config]) => {
                                                const inputStyle = {
                                                    background: '#21262d',
                                                    border: '1px solid #30363d',
                                                    color: '#e6edf3',
                                                    padding: '0.5rem',
                                                    borderRadius: '6px',
                                                    width: '100%'
                                                };

                                                // For channel_* params that are numbers, render as channel selectors
                                                if (key.startsWith('channel_') && config.type === 'number') {
                                                    return (
                                                        <div key={key} style={{ marginBottom: '1rem' }}>
                                                            <label className="form-label" style={{ color: '#e6edf3', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'block' }}>
                                                                {config.label || key}
                                                            </label>
                                                            <select
                                                                className="form-select"
                                                                value={toolParams[key] !== undefined ? toolParams[key] : (config.default || 0)}
                                                                onChange={(e) => setToolParams(prev => ({ ...prev, [key]: parseInt(e.target.value) }))}
                                                                disabled={!selectedSignalId}
                                                                style={inputStyle}
                                                            >
                                                                {selectedSignalId && (() => {
                                                                    const record = signalRecords.find(r => String(r.id) === String(selectedSignalId));
                                                                    if (!record?.metadata?.channels) return <option value={config.default || 0}>Channel {config.default || 0}</option>;
                                                                    return record.metadata.channels.map(ch => (
                                                                        <option key={ch.index} value={ch.index}>
                                                                            {ch.label || `Channel ${ch.index}`} ({ch.sample_rate} Hz)
                                                                        </option>
                                                                    ));
                                                                })()}
                                                            </select>
                                                            {config.description && (
                                                                <small style={{ color: '#8b949e', fontSize: '0.75rem' }}>{config.description}</small>
                                                            )}
                                                        </div>
                                                    );
                                                }

                                                // Regular number input
                                                if (config.type === 'number') {
                                                    return (
                                                        <div key={key} style={{ marginBottom: '1rem' }}>
                                                            <label className="form-label" style={{ color: '#e6edf3', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'block' }}>
                                                                {config.label || key}
                                                            </label>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                value={toolParams[key] !== undefined ? toolParams[key] : (config.default || '')}
                                                                onChange={(e) => setToolParams(prev => ({ ...prev, [key]: parseFloat(e.target.value) || 0 }))}
                                                                step={Number.isInteger(config.default) ? 1 : 0.01}
                                                                style={inputStyle}
                                                            />
                                                            {config.description && (
                                                                <small style={{ color: '#8b949e', fontSize: '0.75rem' }}>{config.description}</small>
                                                            )}
                                                        </div>
                                                    );
                                                }

                                                // String/text input
                                                if (config.type === 'string' || config.type === 'text') {
                                                    return (
                                                        <div key={key} style={{ marginBottom: '1rem' }}>
                                                            <label className="form-label" style={{ color: '#e6edf3', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'block' }}>
                                                                {config.label || key}
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={toolParams[key] !== undefined ? toolParams[key] : (config.default || '')}
                                                                onChange={(e) => setToolParams(prev => ({ ...prev, [key]: e.target.value }))}
                                                                style={inputStyle}
                                                            />
                                                            {config.description && (
                                                                <small style={{ color: '#8b949e', fontSize: '0.75rem' }}>{config.description}</small>
                                                            )}
                                                        </div>
                                                    );
                                                }

                                                // Boolean toggle
                                                if (config.type === 'boolean') {
                                                    return (
                                                        <div key={key} style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={toolParams[key] !== undefined ? toolParams[key] : (config.default || false)}
                                                                onChange={(e) => setToolParams(prev => ({ ...prev, [key]: e.target.checked }))}
                                                                style={{ width: '16px', height: '16px' }}
                                                            />
                                                            <label style={{ color: '#e6edf3', fontSize: '0.9rem' }}>
                                                                {config.label || key}
                                                            </label>
                                                            {config.description && (
                                                                <small style={{ color: '#8b949e', fontSize: '0.75rem', marginLeft: '0.5rem' }}>{config.description}</small>
                                                            )}
                                                        </div>
                                                    );
                                                }

                                                return null;
                                            })}

                                            <div style={{ padding: '1rem', background: 'rgba(88, 166, 255, 0.05)', borderRadius: '6px' }}>
                                                <p style={{ color: '#8b949e', fontSize: '0.8rem', margin: 0 }}>
                                                    <i className="fa fa-info-circle me-1"></i>
                                                    <strong>Note:</strong> This will run as a background task. The output will appear in the Signal Records sidebar when processing is complete.
                                                </p>
                                            </div>
                                        </div>
                                        <div className="modal-footer" style={{ borderTop: '1px solid #30363d', padding: '1rem 1.5rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                                            <button
                                                className="btn btn-secondary"
                                                onClick={() => setShowToolConfig(false)}
                                                style={{ padding: '0.5rem 1rem', borderRadius: '6px' }}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                className="btn btn-primary"
                                                onClick={handleSubmitToolTask}
                                                disabled={!selectedSignalId || isSubmittingHRTask}
                                                style={{ padding: '0.5rem 1rem', borderRadius: '6px', background: selectedTool?.color || '#17a2b8', border: 'none' }}
                                            >
                                                {isSubmittingHRTask ? (
                                                    <>
                                                        <i className="fa fa-spinner fa-spin me-2"></i>
                                                        Submitting...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fa fa-play me-2"></i>
                                                        Run {selectedTool?.name?.split('/').pop() || 'Tool'}
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Tool Executions History */}
                {navbarShows('tool_history') && showTools && toolExecutions.length > 0 && (
                    <div className="tool-executions-section" ref={toolExecutionsDropdownRef}>
                        <button
                            className={`viewer-nav-btn tool-history-btn ${showToolExecutionsDropdown ? 'active' : ''}`}
                            onClick={() => setShowToolExecutionsDropdown(!showToolExecutionsDropdown)}
                            title="Tool Execution History"
                            style={{
                                background: toolExecutions.some(e => e.status === 'running') ? 'linear-gradient(135deg, #17a2b8 0%, #138496 100%)' : 'linear-gradient(135deg, #6c757d 0%, #5a6268 100%)',
                                borderColor: toolExecutions.some(e => e.status === 'running') ? '#1ab3ce' : '#6c757d',
                                position: 'relative'
                            }}
                        >
                            <i className="fa fa-history"></i>
                            <span>Tools History</span>
                            {toolExecutions.some(e => e.status === 'running') && (
                                <i className="fa fa-spinner fa-spin ms-1"></i>
                            )}
                            {toolExecutions.filter(e => e.status === 'running').length > 0 && (
                                <span className="badge bg-warning ms-1" style={{ fontSize: '0.7rem' }}>
                                    {toolExecutions.filter(e => e.status === 'running').length}
                                </span>
                            )}
                        </button>

                        {showToolExecutionsDropdown && (
                            <div className="executions-dropdown-panel" onClick={(e) => e.stopPropagation()}>
                                <div className="executions-dropdown-header">
                                    <i className="fa fa-history"></i>
                                    <span>Tool Execution History</span>
                                    {toolExecutions.length > 0 && (
                                        <button
                                            className="btn btn-sm btn-outline-secondary ms-auto"
                                            onClick={() => {
                                                setToolExecutions(prev => prev.filter(e => e.status === 'running'));
                                                if (toolExecutions.filter(e => e.status === 'running').length === 0) {
                                                    setShowToolExecutionsDropdown(false);
                                                }
                                            }}
                                            style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
                                        >
                                            Clear Completed
                                        </button>
                                    )}
                                </div>

                                <div className="executions-dropdown-body">
                                    {toolExecutions.length === 0 ? (
                                        <div className="empty-executions" style={{ padding: '2rem', textAlign: 'center', color: '#8b949e' }}>
                                            <i className="fa fa-history" style={{ fontSize: '2rem', marginBottom: '0.5rem', opacity: 0.3 }}></i>
                                            <p style={{ margin: 0, fontSize: '0.9rem' }}>No tool executions yet</p>
                                        </div>
                                    ) : (
                                        toolExecutions.map(exec => (
                                            <div key={exec.id} className={`execution-item ${exec.status}`}>
                                                <div className="execution-header">
                                                    <div className="execution-icon">
                                                        <i className={`fa ${exec.toolIcon}`}></i>
                                                    </div>
                                                    <div className="execution-info">
                                                        <div className="execution-name">{exec.toolName}</div>
                                                        <div className="execution-config" style={{ fontSize: '0.75rem', color: '#8b949e', marginTop: '0.2rem' }}>
                                                            Signal: {exec.config.signalName} | Channel: {exec.config.channelName}
                                                        </div>
                                                        <div className="execution-time" style={{ fontSize: '0.7rem', color: '#6c757d', marginTop: '0.2rem' }}>
                                                            {exec.status === 'running' && elapsedTimes[exec.id] !== undefined ? (
                                                                <>
                                                                    <i className="fa fa-clock-o me-1"></i>
                                                                    Running for {Math.floor(elapsedTimes[exec.id] / 60)}m {elapsedTimes[exec.id] % 60}s
                                                                </>
                                                            ) : (
                                                                <>
                                                                    {exec.startTime.toLocaleTimeString()}
                                                                    {exec.endTime && ` → ${exec.endTime.toLocaleTimeString()}`}
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="execution-status">
                                                        {exec.status === 'running' && (
                                                            <span className="badge bg-primary">
                                                                <i className="fa fa-spinner fa-spin me-1"></i>
                                                                Running
                                                            </span>
                                                        )}
                                                        {exec.status === 'completed' && (
                                                            <span className="badge bg-success">
                                                                <i className="fa fa-check me-1"></i>
                                                                Completed
                                                            </span>
                                                        )}
                                                        {exec.status === 'failed' && (
                                                            <span className="badge bg-danger">
                                                                <i className="fa fa-times me-1"></i>
                                                                Failed
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                {exec.status === 'running' && (
                                                    <div className="execution-progress">
                                                        <div className="progress" style={{ height: '4px', marginTop: '0.5rem' }}>
                                                            <div
                                                                className="progress-bar progress-bar-striped progress-bar-animated"
                                                                style={{ width: '100%', background: '#17a2b8' }}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                )}
                                                {exec.status === 'failed' && exec.error && (
                                                    <div className="execution-error" style={{ marginTop: '0.5rem', padding: '0.5rem', background: 'rgba(220, 53, 69, 0.1)', borderRadius: '4px' }}>
                                                        <small style={{ color: '#dc3545' }}>{exec.error}</small>
                                                    </div>
                                                )}
                                                {exec.status === 'completed' && exec.result && (
                                                    <div className="execution-result" style={{ marginTop: '0.5rem', padding: '0.5rem', background: 'rgba(40, 167, 69, 0.1)', borderRadius: '4px' }}>
                                                        <small style={{ color: '#28a745' }}>
                                                            <i className="fa fa-check-circle me-1"></i>
                                                            Created signal: {exec.result.filename}
                                                        </small>
                                                    </div>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Request Input */}
                {navbarShows('request_bar') && (
                    <div className="viewer-request-container">
                        <div className="viewer-request-input-wrapper">
                            <i className="fa fa-magic request-icon"></i>
                            <input
                                type="text"
                                className="viewer-request-input"
                                placeholder="Enter a request... (press Enter)"
                                value={requestInput}
                                onChange={(e) => setRequestInput(e.target.value)}
                                onKeyDown={handleRequestSubmit}
                                disabled={isProcessingRequest}
                            />
                            {isProcessingRequest && (
                                <div className="request-spinner">
                                    <i className="fa fa-spinner fa-spin"></i>
                                </div>
                            )}
                        </div>
                        {/* Progress Bar */}
                        {isProcessingRequest && (
                            <div className="viewer-request-progress">
                                <div
                                    className="viewer-request-progress-bar"
                                    style={{ width: `${requestProgress}%` }}
                                ></div>
                                <span className="viewer-request-status">{requestStatus}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Save Annotations (OHIF measurements + segmentations) */}
                {navbarShows('save_ohif_annotations') && patientId && (
                    <button
                        type="button"
                        className={`viewer-save-ohif ${isSavingOhif ? 'is-loading' : ''}`}
                        onClick={handleSaveOhifAnnotations}
                        disabled={isSavingOhif}
                        title="Save measurements and segmentations from the OHIF viewer"
                    >
                        <i className={`fa ${isSavingOhif ? 'fa-spinner fa-spin' : 'fa-save'}`}></i>
                        <span>Save Annotations</span>
                    </button>
                )}

                {/* Mark as Annotated checkbox */}
                {navbarShows('mark_as_annotated') && patientId && (
                    <label
                        className={`viewer-mark-annotated ${markedAsAnnotated ? 'is-checked' : ''} ${isTogglingAnnotated ? 'is-loading' : ''}`}
                        title="Mark this patient as annotated"
                    >
                        <input
                            type="checkbox"
                            checked={markedAsAnnotated}
                            onChange={toggleMarkedAsAnnotated}
                            disabled={isTogglingAnnotated}
                        />
                        <span className="viewer-mark-annotated-label">
                            <i className={`fa ${markedAsAnnotated ? 'fa-check-square-o' : 'fa-square-o'}`}></i>
                            Mark as Annotated
                        </span>
                    </label>
                )}
            </div>
        </nav>

        {/* Labels Side Panel */}
        {filteredLabels.length > 0 && labelDisplayMode === 'side_panel' && (
            <div className={`labels-side-panel ${labelsPanelOpen ? 'open' : 'closed'}`}>
                <div className="labels-side-panel-inner">
                    <div className="labels-side-panel-header">
                        <div className="labels-side-panel-title">
                            <i className="fa fa-tags"></i>
                            <span>Labels</span>
                            {selectedLabels.length > 0 && (
                                <span className="labels-count-badge">{selectedLabels.length}/{filteredLabels.length}</span>
                            )}
                        </div>
                        <button
                            className="labels-side-panel-toggle"
                            onClick={() => setLabelsPanelOpen(prev => !prev)}
                            title={labelsPanelOpen ? 'Collapse' : 'Expand'}
                        >
                            <i className={`fa fa-chevron-${labelsPanelOpen ? 'right' : 'left'}`}></i>
                        </button>
                    </div>
                    {isSingleSelectionMode && (
                        <div className="labels-side-panel-hint">Single selection</div>
                    )}
                    <div className="labels-side-panel-list">
                        {filteredLabels.map((label) => (
                            <button
                                key={label.id}
                                className={`labels-side-panel-item ${isLabelSelected(label) ? 'selected' : ''}`}
                                onClick={() => handleToggleLabel(label)}
                            >
                                <span
                                    className="label-color-dot"
                                    style={{ backgroundColor: label.color }}
                                ></span>
                                <span className="label-item-name">{label.name}</span>
                                {label.shortcut && (
                                    <span className="label-item-shortcut">{label.shortcut}</span>
                                )}
                                {isLabelSelected(label) && (
                                    <i className="fa fa-check label-selected-check"></i>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
                <button
                    className="labels-side-panel-tab"
                    onClick={() => setLabelsPanelOpen(prev => !prev)}
                    title={labelsPanelOpen ? 'Collapse labels' : 'Expand labels'}
                >
                    <i className={`fa fa-chevron-${labelsPanelOpen ? 'right' : 'left'}`}></i>
                </button>
            </div>
        )}
        </>
    );
};

export default ViewerNavbar;
